/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * The reducer for Money Transfer wizard
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.11.0
 * @since 1.2.0
 */

import produce from 'immer';
import { Reducer } from 'redux';

import { IAccountSelectItem } from '../../components/AccountSelect/AccountSelectTypes';
import { IWizardValidationError } from '../../modules/wizard/wizardTypes';
import { LOGOUT_START_SUCCESSFUL } from '../PageAuth/constants/authConstants';
import { TMoneyTransferActions } from './moneyTransferActions';
import {
    MONEY_TRANSFER_CHECK_RECIPIENTS_FINISH,
    MONEY_TRANSFER_CHECK_RECIPIENTS_START,
    MONEY_TRANSFER_CLEAR_SEARCH_VALUE,
    MONEY_TRANSFER_HANDLE_NEXT,
    MONEY_TRANSFER_LOAD_ACCOUNT_LIST,
    MONEY_TRANSFER_LOAD_ACCOUNT_LIST_FAILED,
    MONEY_TRANSFER_LOAD_ACCOUNT_LIST_SUCCESSFUL,
    MONEY_TRANSFER_SET_ACCOUNT,
    MONEY_TRANSFER_SET_AMOUNT,
    MONEY_TRANSFER_SET_CONFLICTS,
    MONEY_TRANSFER_SET_PARAMS,
    MONEY_TRANSFER_SET_PARAMS_ERROR_CODE,
    MONEY_TRANSFER_SET_SEARCH_VALUE,
    MONEY_TRANSFER_VALIDATE_SET_STATUS,
} from './moneyTransferConstants';
import { EMoneyTransferParamType, IMoneyTransferAccountParam } from './types/moneyTransferTypes';

export interface IMoneyTransferReducer {
    [EMoneyTransferParamType.transferAccountFrom]: IMoneyTransferAccountParam;
    [EMoneyTransferParamType.transferAccountTo]: IMoneyTransferAccountParam;
    conflicts: IWizardValidationError[];
    isCheckRecipientsLoading: boolean;
    isPageValid: boolean;
    transferAmount: {
        value: string;
        errorCode: string;
    };
    validateError: string;
}

export const initTransferAccount: IAccountSelectItem = {
    balance: 0,
    id: '',
    name: '',
    number: '',
};

export const initMoneyTransferAccountParam: IMoneyTransferAccountParam = {
    accountList: [],
    isLoading: false,
    errorCode: '',
    hasMore: true,
    message: '',
    searchValue: '',
    selectedAccount: { ...initTransferAccount },
};

export const initialMoneyTransferState: IMoneyTransferReducer = {
    [EMoneyTransferParamType.transferAccountFrom]: { ...initMoneyTransferAccountParam },
    [EMoneyTransferParamType.transferAccountTo]: { ...initMoneyTransferAccountParam },
    conflicts: [],
    isCheckRecipientsLoading: false,
    isPageValid: false,
    transferAmount: {
        value: '',
        errorCode: '',
    },
    validateError: '',
};

const moneyTransferReducer: Reducer<IMoneyTransferReducer, TMoneyTransferActions> =
    (state = initialMoneyTransferState, action: TMoneyTransferActions) =>
        produce(state, draft => {
            switch (action.type) {
                case MONEY_TRANSFER_SET_SEARCH_VALUE:
                    draft[action.payload.paramType].searchValue = action.payload.searchValue;
                    break;
                case MONEY_TRANSFER_CLEAR_SEARCH_VALUE:
                    draft[action.payload.paramType].searchValue = '';
                    draft[action.payload.paramType].accountList = [];
                    break;
                case MONEY_TRANSFER_SET_ACCOUNT:
                    draft[action.payload.paramType].selectedAccount = action.payload.account;
                    if (action.payload.paramType === EMoneyTransferParamType.transferAccountFrom) {
                        draft[EMoneyTransferParamType.transferAccountTo] = initMoneyTransferAccountParam;
                        draft.transferAmount = initialMoneyTransferState.transferAmount;
                    }
                    draft[action.payload.paramType].errorCode = '';
                    draft.isPageValid = false;
                    draft.validateError = '';
                    break;
                case MONEY_TRANSFER_SET_PARAMS_ERROR_CODE:
                    if (action.payload.accountFrom !== undefined)
                        draft[EMoneyTransferParamType.transferAccountFrom].errorCode =
                            action.payload.accountFrom;
                    if (action.payload.accountTo !== undefined)
                        draft[EMoneyTransferParamType.transferAccountTo].errorCode =
                            action.payload.accountTo;
                    if (action.payload.amount !== undefined) draft.transferAmount.errorCode = action.payload.amount;
                    break;
                case MONEY_TRANSFER_SET_PARAMS:
                    draft[EMoneyTransferParamType.transferAccountFrom].selectedAccount = action.payload.accountFrom;
                    draft[EMoneyTransferParamType.transferAccountFrom].errorCode = '';
                    draft[EMoneyTransferParamType.transferAccountTo].selectedAccount = action.payload.accountTo;
                    draft[EMoneyTransferParamType.transferAccountTo].errorCode = '';
                    draft.transferAmount.value = action.payload.amount;
                    draft.transferAmount.errorCode = '';
                    draft.isPageValid = false;
                    draft.validateError = '';
                    break;
                case MONEY_TRANSFER_SET_AMOUNT:
                    draft.transferAmount.value = action.payload;
                    draft.transferAmount.errorCode = '';
                    draft.isPageValid = false;
                    draft.validateError = '';
                    break;
                case MONEY_TRANSFER_LOAD_ACCOUNT_LIST:
                    if (action.payload.start) draft[action.payload.paramType].accountList = [];
                    draft[action.payload.paramType].isLoading = action.payload.isLoading === undefined ?
                        true : action.payload.isLoading;
                    draft[action.payload.paramType].message = '';
                    break;
                case MONEY_TRANSFER_LOAD_ACCOUNT_LIST_SUCCESSFUL:
                    draft[action.payload.paramType].accountList = action.payload.data;
                    draft[action.payload.paramType].isLoading = false;
                    draft[action.payload.paramType].hasMore = action.payload.hasMore;
                    draft[action.payload.paramType].message = '';
                    break;
                case MONEY_TRANSFER_LOAD_ACCOUNT_LIST_FAILED:
                    draft[action.payload.paramType].message = action.payload.message;
                    draft[action.payload.paramType].isLoading = false;
                    break;
                case MONEY_TRANSFER_VALIDATE_SET_STATUS:
                    draft.isPageValid = action.payload.isStepValid;
                    draft.validateError = action.payload.validateError;
                    break;
                case MONEY_TRANSFER_HANDLE_NEXT:
                    break;
                case MONEY_TRANSFER_SET_CONFLICTS:
                    draft.conflicts = action.payload;
                    break;
                case MONEY_TRANSFER_CHECK_RECIPIENTS_START:
                    draft.isCheckRecipientsLoading = true;
                    break;
                case MONEY_TRANSFER_CHECK_RECIPIENTS_FINISH:
                    draft.isCheckRecipientsLoading = false;
                    break;

                case LOGOUT_START_SUCCESSFUL:
                    return initialMoneyTransferState;

                default:
                    break;
            }
        });

export default moneyTransferReducer;
